/**
 * Badge Component — HMVMReviewer Design System Phase 1
 * 
 * Features:
 * - Generic badge with customizable color
 * - Status badges (completed, running, pending, failed, etc.)
 * - Severity badges (critical, high, medium, low)
 * - Semantic color system (independent of product accent)
 * - Size variants (sm, md, lg)
 * - Square geometry (border-radius: 0)
 */

import React, { HTMLAttributes } from 'react';
import { useLocale } from '../../contexts/LocaleContext';
import { severityLabel, statusLabel } from '../../lib/i18n';

export type BadgeVariant = 
  | 'default'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'accent';

export type BadgeSize = 'sm' | 'md' | 'lg';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  size?: BadgeSize;
  children: React.ReactNode;
}

const variantStyles: Record<BadgeVariant, string> = {
  default: `
    text-[var(--reviewer-text-secondary)]
    border-[var(--reviewer-border-strong)]
    bg-[var(--reviewer-bg-elevated)]
  `,
  success: `
    text-[var(--reviewer-success)]
    border-[var(--reviewer-success-border)]
    bg-[var(--reviewer-success-soft)]
  `,
  warning: `
    text-[var(--reviewer-warning)]
    border-[var(--reviewer-warning-border)]
    bg-[var(--reviewer-warning-soft)]
  `,
  danger: `
    text-[var(--reviewer-danger)]
    border-[var(--reviewer-danger-border)]
    bg-[var(--reviewer-danger-soft)]
  `,
  info: `
    text-[var(--reviewer-info)]
    border-[var(--reviewer-info-border)]
    bg-[var(--reviewer-info-soft)]
  `,
  accent: `
    text-[var(--reviewer-accent)]
    border-[var(--reviewer-accent-border)]
    bg-[var(--reviewer-accent-soft)]
  `,
};

const sizeStyles: Record<BadgeSize, string> = {
  sm: 'px-1.5 py-0.5 text-[var(--reviewer-text-xs)]',
  md: 'px-2 py-1 text-[var(--reviewer-text-sm)]',
  lg: 'px-3 py-1.5 text-[var(--reviewer-text-base)]',
};

export function Badge({
  variant = 'default',
  size = 'md',
  className = '',
  children,
  ...props
}: BadgeProps) {
  return (
    <span
      className={`
        inline-flex items-center justify-center
        border font-medium font-mono
        uppercase tracking-wider
        ${variantStyles[variant]}
        ${sizeStyles[size]}
        ${className}
      `}
      {...props}
    >
      {children}
    </span>
  );
}

/**
 * Specialized Status Badge
 */
export type Status = 
  | 'completed' 
  | 'running' 
  | 'pending' 
  | 'queued'
  | 'failed' 
  | 'partial'
  | 'cancelled'
  | 'timeout'
  | 'open'
  | 'resolved'
  | 'false_positive'
  | 'wont_fix'
  | 'investigating';

const statusVariantMap: Record<Status, BadgeVariant> = {
  completed: 'success',
  running: 'info',
  pending: 'default',
  queued: 'default',
  failed: 'danger',
  partial: 'warning',
  cancelled: 'default',
  timeout: 'danger',
  open: 'warning',
  resolved: 'success',
  false_positive: 'default',
  wont_fix: 'default',
  investigating: 'info',
};

export interface StatusBadgeProps extends Omit<BadgeProps, 'variant' | 'children'> {
  status: Status | string;
}

export function StatusBadge({ status, className = '', ...props }: StatusBadgeProps) {
  const { locale } = useLocale();
  const statusLower = status.toLowerCase() as Status;
  const variant = statusVariantMap[statusLower] || 'default';
  const label =
    locale === 'en'
      ? status.toUpperCase().replace(/_/g, ' ')
      : statusLabel(locale, statusLower);
  const caseClass = locale === 'en' ? '' : 'normal-case tracking-normal';
  
  return (
    <Badge variant={variant} className={`${caseClass} ${className}`.trim()} {...props}>
      {label}
    </Badge>
  );
}

/**
 * Specialized Severity Badge
 */
export type Severity = 'critical' | 'high' | 'medium' | 'low' | 'info';

const severityVariantMap: Record<Severity, BadgeVariant> = {
  critical: 'danger',
  high: 'warning',
  medium: 'warning',
  low: 'info',
  info: 'default',
};

export interface SeverityBadgeProps extends Omit<BadgeProps, 'variant' | 'children'> {
  severity: Severity | string;
}

export function SeverityBadge({ severity, className = '', ...props }: SeverityBadgeProps) {
  const { locale } = useLocale();
  const severityLower = severity.toLowerCase() as Severity;
  const variant = severityVariantMap[severityLower] || 'default';
  const label =
    locale === 'en'
      ? severity.toUpperCase()
      : severityLabel(locale, severityLower);
  const caseClass = locale === 'en' ? '' : 'normal-case tracking-normal';
  
  return (
    <Badge variant={variant} className={`${caseClass} ${className}`.trim()} {...props}>
      {label}
    </Badge>
  );
}
